import type { Term, Tone } from "@/lib/campaign";
import type {
  WebhookEndpointInput, WebhookEndpointUpdateInput, WebhookSubscriptionInput,
  WebhookSubscriptionUpdateInput,
} from "@/lib/queries";
import type { WebhookDelivery, WebhookEndpoint, WebhookField, WebhookSubscription } from "@/lib/types";

/**
 * Webhooks as the console shows and edits them (ADR-022): what each event is, where a delivery
 * stands, and the arithmetic between the forms and core-api's `/webhooks/*` routes.
 *
 * The rules are `platform`'s. The header rules are `webhook_headers_problem` and the methods
 * `WEBHOOK_METHODS` in `services/shared/database/models/integrations.py`; a condition's path is
 * `_check_webhook_conditions` in core-api's `schemas.py`; a template renders as the webhooks
 * service's `src/templates.py` renders it. The server decides all of it. The copies here keep what
 * it would refuse off the form, say why first, and show a template as it will be sent. Pure, for
 * `test/webhooks.test.ts`.
 */

// --- what each event is --------------------------------------------------------------------------

export interface EventTerm extends Term {
  /** Whether a platform service publishes the event today. A subscription to one that is not is
   *  saved, and hears nothing until one does. */
  sent: boolean;
}

/**
 * Each contract event in the words of someone wiring it to another system. `sent` was read off the
 * platform's publishers on 2026-09-14: core-api (`disposition.set`, `compliance.blocked`), the ACD
 * (`call.bridged`), the dialer (`call.originated`, `call.abandoned`, `call.vm_drop.result`,
 * `call.ended`, `compliance.blocked`), the event gateway (`call.network_answer`, `call.ended`) and
 * live transcription (`call.transcript`); and on 2026-09-17 the ACD's caller events
 * (`call.rep_connected`, `call.handoff_requested`, `call.handed_off`, `call.missed`, platform
 * ADR-038), whose context also says who the caller and the reps are; and on 2026-10-12 core-api's
 * two text events (`message.received`, `message.sent`, platform ADR-130), whose context names the
 * reps a text is for or from.
 */
const EVENTS: Record<string, EventTerm> = {
  "disposition.set": {
    label: "Call outcome chosen",
    description: "A rep chose how a lead's call went: signed, a callback, not interested. It carries the outcome and the rep's note.",
    sent: true,
  },
  "call.ended": {
    label: "Call ended",
    description: "A call with a lead hung up: how it ended, how long it rang and talked, and whether it was recorded.",
    sent: true,
  },
  "call.bridged": {
    label: "Call connected to a rep",
    description: "A lead's call reached a rep: an inbound caller answered from a line, or a dialed lead put through.",
    sent: true,
  },
  "call.rep_connected": {
    label: "Caller reached a rep",
    description: "A rep is on the call with a caller: their first rep, or a closer the call was handed to. Every caller, lead or not; it says who the caller and the rep are.",
    sent: true,
  },
  "call.handoff_requested": {
    label: "Rep sent a caller to a closer",
    description: "A rep asked for a closer for their caller, straight away or after briefing one. It carries the rep's note.",
    sent: true,
  },
  "call.handed_off": {
    label: "Closer took over a caller",
    description: "A handoff went through: the closer has the caller. It says who sent them, who took them, and the note.",
    sent: true,
  },
  "call.missed": {
    label: "Missed call",
    description: "A caller never reached a rep: they hung up waiting, the line's longest wait ran out, or the rep whose number they called could not answer. It says which.",
    sent: true,
  },
  "call.originated": {
    label: "Dialer placed a call",
    description: "The dialer started calling a lead.",
    sent: true,
  },
  "call.network_answer": {
    label: "Call answered",
    description: "The far end picked up, before anyone knows whether a person or a machine did.",
    sent: true,
  },
  "call.abandoned": {
    label: "Call dropped by the dialer",
    description: "A dialed lead answered and no rep was free in time, so the dialer let them go.",
    sent: true,
  },
  "call.vm_drop.result": {
    label: "Voicemail left",
    description: "A rep dropped a recorded message on a lead's voicemail greeting, and whether it played to the end.",
    sent: true,
  },
  "call.transcript": {
    label: "Transcript ready",
    description: "A call's transcript is complete: who said what, and when.",
    sent: true,
  },
  "message.received": {
    label: "Text received",
    description: "Somebody texted one of our numbers. It carries their number and what they wrote, whose inbox the text landed in, and whether it was a STOP.",
    sent: true,
  },
  "message.sent": {
    label: "Text sent",
    description: "Somebody on the team texted a person and the provider took the text. It carries the number, the words as they left and who sent it. Whether it was delivered is not sent.",
    sent: true,
  },
  "compliance.blocked": {
    label: "Lead blocked from calls",
    description: "A lead was marked do-not-call, or the dialer refused to call one: no consent, or quiet hours.",
    sent: true,
  },
  "call.human_answer": {
    label: "A person answered",
    description: "Answering-machine detection heard a person. Not sent yet: detection is not live.",
    sent: false,
  },
  "call.recording.available": {
    label: "Recording ready",
    description: "A call's recording was stored. Not sent yet.",
    sent: false,
  },
  "lead.created": {
    label: "Lead created",
    description: "A new lead was made: from intake, a trigger, a CSV import, the console or a hand dial. It carries how it came in and its fields.",
    sent: true,
  },
  "lead.assigned": {
    label: "Lead given to a rep",
    description: "A new lead was given to a rep Ready on its campaign's line, the Ready reps in turn, or to the rep who dialed it by hand. It names the rep and the line.",
    sent: true,
  },
  "lead.not_assigned": {
    label: "Lead given to nobody",
    description: "A new lead could not be given to a rep, and why: it is on no campaign, its campaign has no line, nobody was Ready, or the reps' status could not be read.",
    sent: true,
  },
  "lead.unattended": {
    label: "Lead still unattended",
    description: "A lead given to nobody that nobody has called, texted, given an outcome or taken since: every five minutes, for up to an hour.",
    sent: true,
  },
  "cadence.step.due": {
    label: "Cadence step due",
    description: "A lead's next cadence step came due. Not sent yet: cadences are not built.",
    sent: false,
  },
  "cadence.step.skipped": {
    label: "Cadence step skipped",
    description: "A lead's cadence step was passed over. Not sent yet: cadences are not built.",
    sent: false,
  },
};

export function eventTerm(type: string): EventTerm {
  return (
    EVENTS[type] ?? { label: type, description: "An event this console has no words for yet.", sent: true }
  );
}

/** The picker's order: what is sent today first, then each group by its label. */
export function eventTypeOrder(types: readonly string[]): string[] {
  return [...types].sort((a, b) => {
    const x = eventTerm(a);
    const y = eventTerm(b);
    if (x.sent !== y.sent) return x.sent ? -1 : 1;
    return x.label.localeCompare(y.label);
  });
}

// --- where a delivery stands -----------------------------------------------------------------------

export const DELIVERY_STATUSES = ["pending", "delivered", "failed", "dead"] as const;

const DELIVERY: Record<string, Term & { tone: Tone }> = {
  pending: {
    label: "Sending",
    description: "Queued. The webhooks service sends it within a few seconds.",
    tone: "neutral",
  },
  delivered: { label: "Delivered", description: "The receiver answered with a 2xx.", tone: "success" },
  failed: {
    label: "Retrying",
    description: "No answer, or a 408, 429 or 5xx. It is tried again after a wait, for about fifteen hours.",
    tone: "warning",
  },
  dead: {
    label: "Failed",
    description: "Given up on: another 4xx, a redirect, a URL the platform may not call, or no success by the last try.",
    tone: "danger",
  },
};

export function deliveryStatusText(status: string): Term & { tone: Tone } {
  return DELIVERY[status] ?? { label: status, description: "Not a status this console knows.", tone: "outline" };
}

/** Whether the webhooks service is done with a delivery, which is when it can be sent again. */
export function isSettled(status: string): boolean {
  return status === "delivered" || status === "dead";
}

/** What the receiver said last, in one line. */
export function lastAnswer(
  delivery: Pick<WebhookDelivery, "attempts" | "last_status" | "last_error">,
): string {
  if (delivery.attempts === 0) return "Not sent yet";
  if (delivery.last_error) return delivery.last_error;
  return delivery.last_status !== null ? `HTTP ${delivery.last_status}` : "No answer";
}

// --- an endpoint -----------------------------------------------------------------------------------

export const WEBHOOK_METHODS = ["POST", "PUT", "PATCH"] as const;

/** An HTTP token (RFC 9110), as `_HEADER_NAME_RE`. */
const HEADER_NAME_RE = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
/** Printable ASCII, with spaces and tabs only between characters, as `_HEADER_VALUE_RE`. */
const HEADER_VALUE_RE = /^(?:[\x21-\x7e]+(?:[ \t]+[\x21-\x7e]+)*)?$/;
/** What HTTP sets. Every `X-ACL-` name is the webhooks service's own. */
const RESERVED_HEADERS = new Set(["connection", "content-length", "content-type", "host", "transfer-encoding"]);
const RESERVED_PREFIX = "x-acl-";

export interface HeaderRow {
  name: string;
  value: string;
}

export function headerRows(headers: Readonly<Record<string, string>>): HeaderRow[] {
  return Object.entries(headers).map(([name, value]) => ({ name, value }));
}

function blank(row: HeaderRow): boolean {
  return !row.name.trim() && !row.value;
}

/** Why a header cannot be sent as it stands, or null when it can. */
export function headerProblem(row: HeaderRow): string | null {
  const name = row.name.trim();
  if (!HEADER_NAME_RE.test(name)) {
    return "A header name is letters, digits and !#$%&'*+-.^_`|~, with no spaces.";
  }
  const lowered = name.toLowerCase();
  if (RESERVED_HEADERS.has(lowered) || lowered.startsWith(RESERVED_PREFIX)) {
    return `${name} is set on every delivery by HTTP or the platform. Name another header.`;
  }
  if (!HEADER_VALUE_RE.test(row.value)) {
    return "A header value is plain ASCII, with no line break and no space at either end.";
  }
  return null;
}

/** The first problem among the rows, a name given twice included. A row left blank is ignored. */
export function headersProblem(rows: readonly HeaderRow[]): string | null {
  const seen = new Set<string>();
  for (const row of rows) {
    if (blank(row)) continue;
    const problem = headerProblem(row);
    if (problem) return problem;
    const lowered = row.name.trim().toLowerCase();
    if (seen.has(lowered)) return `${row.name.trim()} is named twice.`;
    seen.add(lowered);
  }
  return null;
}

/** The rows as the object core-api stores. A row left blank is dropped. */
export function headersObject(rows: readonly HeaderRow[]): Record<string, string> {
  return Object.fromEntries(rows.filter((row) => !blank(row)).map((row) => [row.name.trim(), row.value]));
}

/** Why a URL cannot be an endpoint's, or null. Where it may point is the SSRF guard's call, on save. */
export function urlProblem(url: string): string | null {
  const text = url.trim();
  if (!text) return "Required";
  let parsed: URL;
  try {
    parsed = new URL(text);
  } catch {
    return "Not a URL. It starts with https://";
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return "Only an http or https URL can be called.";
  }
  return parsed.hostname ? null : "A URL needs a host.";
}

export interface EndpointValues {
  name: string;
  url: string;
  method: string;
  headers: HeaderRow[];
  enabled: boolean;
}

export const NEW_ENDPOINT: EndpointValues = { name: "", url: "", method: "POST", headers: [], enabled: true };

export function endpointValues(endpoint: WebhookEndpoint): EndpointValues {
  return {
    name: endpoint.name,
    url: endpoint.url ?? "",
    method: endpoint.method,
    headers: headerRows(endpoint.headers),
    enabled: endpoint.enabled,
  };
}

/** What `POST /webhooks/endpoints` takes. */
export function endpointPayload(values: EndpointValues): Required<Pick<WebhookEndpointInput, "name" | "url" | "method" | "headers" | "enabled">> {
  return {
    name: values.name.trim(),
    url: values.url.trim(),
    method: values.method,
    headers: headersObject(values.headers),
    enabled: values.enabled,
  };
}

function sameRecord(a: Readonly<Record<string, unknown>>, b: Readonly<Record<string, unknown>>): boolean {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => (a[key] === null || b[key] === null ? a[key] === b[key] : String(a[key]) === String(b[key])));
}

/** The fields an edit changed, as `PUT /webhooks/endpoints/{id}` takes them. Headers go whole. */
export function endpointChanges(values: EndpointValues, endpoint: WebhookEndpoint): WebhookEndpointUpdateInput {
  const next = endpointPayload(values);
  const out: WebhookEndpointUpdateInput = {};
  if (next.name !== endpoint.name) out.name = next.name;
  if (next.url !== endpoint.url) out.url = next.url;
  if (next.method !== endpoint.method) out.method = next.method;
  if (!sameRecord(next.headers, endpoint.headers)) out.headers = next.headers;
  if (next.enabled !== endpoint.enabled) out.enabled = next.enabled;
  return out;
}

// --- a subscription --------------------------------------------------------------------------------

/** A path into the event: dotted steps, with no list index, which the service cannot follow. */
export const CONDITION_PATH_RE = /^(?:\$\.)?[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+){0,15}$/;
/** `source_type: tenant`: every campaign of the endpoint's tenant. */
export const EVERY_CAMPAIGN = "";

export interface ConditionRow {
  path: string;
  value: string;
  empty?: boolean;
}

export interface SubscriptionValues {
  event_type: string;
  /** A campaign's id, or `EVERY_CAMPAIGN`. */
  campaign_id: string;
  conditions: ConditionRow[];
  body_mode: string;
  post_in?: "thread" | "channel" | null;
  template: string;
  enabled: boolean;
}

export const NEW_SUBSCRIPTION: SubscriptionValues = {
  event_type: "",
  campaign_id: EVERY_CAMPAIGN,
  conditions: [],
  body_mode: "event",
  template: "",
  enabled: true,
};

export function subscriptionValues(subscription: WebhookSubscription): SubscriptionValues {
  return {
    event_type: subscription.event_type,
    campaign_id: subscription.source_type === "campaign" ? (subscription.source_id ?? "") : EVERY_CAMPAIGN,
    conditions: Object.entries(subscription.conditions).map(([path, value]) => ({ path, value: value === null ? "" : String(value), ...(value === null ? { empty: true } : {}) })),
    body_mode: subscription.body_mode,
    ...(subscription.post_in ? { post_in: subscription.post_in } : {}),
    template: subscription.template ?? "",
    enabled: subscription.enabled,
  };
}

function blankCondition(row: ConditionRow): boolean {
  return !row.path.trim() && !row.value.trim();
}

/** Why a subscription cannot be saved as it stands, or null. A condition row left blank is ignored. */
export function subscriptionProblem(values: SubscriptionValues): string | null {
  if (!values.event_type) return "Choose the event to send.";
  const seen = new Set<string>();
  for (const row of values.conditions) {
    if (blankCondition(row)) continue;
    const path = row.path.trim();
    if (!CONDITION_PATH_RE.test(path)) {
      return `"${path}" is not a path into the event. Write one like payload.disposition, with no list index.`;
    }
    if (seen.has(path)) return `${path} has two conditions. An event holds one value there.`;
    seen.add(path);
  }
  if (values.body_mode === "template" && !values.template.trim()) {
    return "A template body needs a template.";
  }
  return null;
}

/**
 * The conditions as core-api stores them. A value goes as the text typed, because the service
 * compares text: `true` in an event matches the text `true`, and `3` matches `3`.
 */
export function conditionsObject(rows: readonly ConditionRow[]): Record<string, string | null> {
  return Object.fromEntries(
    rows.filter((row) => !blankCondition(row)).map((row) => [row.path.trim(), row.empty ? null : row.value.trim()]),
  );
}

/** What `POST /webhooks/endpoints/{id}/subscriptions` takes. */
export function subscriptionPayload(values: SubscriptionValues): Required<Omit<WebhookSubscriptionInput, "post_in">> & Pick<WebhookSubscriptionInput, "post_in"> {
  const campaign = values.campaign_id !== EVERY_CAMPAIGN;
  const template = values.body_mode === "template";
  return {
    event_type: values.event_type,
    source_type: campaign ? "campaign" : "tenant",
    source_id: campaign ? values.campaign_id : null,
    conditions: conditionsObject(values.conditions),
    body_mode: values.body_mode,
    ...(values.post_in ? { post_in: values.post_in } : {}),
    template: template ? values.template : null,
    enabled: values.enabled,
  };
}

/** The fields an edit changed, as `PUT .../subscriptions/{id}` takes them. A campaign goes with its
 *  source type, so moving to every campaign clears it. */
export function subscriptionChanges(
  values: SubscriptionValues,
  subscription: WebhookSubscription,
): WebhookSubscriptionUpdateInput {
  const next = subscriptionPayload(values);
  const out: WebhookSubscriptionUpdateInput = {};
  if (next.event_type !== subscription.event_type) out.event_type = next.event_type;
  if (next.source_type !== subscription.source_type || next.source_id !== subscription.source_id) {
    out.source_type = next.source_type;
    out.source_id = next.source_id;
  }
  if (!sameRecord(next.conditions, subscription.conditions)) out.conditions = next.conditions;
  if (next.post_in !== subscription.post_in && next.post_in) out.post_in = next.post_in;
  if (next.body_mode !== subscription.body_mode) out.body_mode = next.body_mode;
  if (next.template !== (subscription.template ?? null)) out.template = next.template;
  if (next.enabled !== subscription.enabled) out.enabled = next.enabled;
  return out;
}

/** A subscription's conditions in one line: `payload.disposition is signed`. */
export function conditionsText(conditions: Readonly<Record<string, string | number | boolean | null>>): string | null {
  const entries = Object.entries(conditions);
  if (entries.length === 0) return null;
  return entries.map(([path, value]) => `${path} is ${value === null ? "empty" : String(value)}`).join(" and ");
}

// --- templates and samples -------------------------------------------------------------------------

/** `{{path.to.field}}`, as `TOKEN_RE` in the service's `templates.py`. */
const TOKEN_RE = /\{\{\s*((?:\$\.)?[\w.]+)\s*\}\}/g;

/** The value at a dotted path into an event, or undefined: `matching.resolve_path`. A list is not
 *  stepped into. */
export function resolvePath(doc: unknown, path: string): unknown {
  let node: unknown = doc;
  for (const key of path.replace(/^\$\./, "").split(".")) {
    if (node === null || typeof node !== "object" || Array.isArray(node) || !Object.hasOwn(node, key)) {
      return undefined;
    }
    node = (node as Record<string, unknown>)[key];
  }
  return node;
}

function valueText(value: unknown): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

/**
 * A template rendered over an event, as the service sends it: every value JSON-escaped, so a quote
 * in a rep's note cannot break the body. A token inside quotes always makes a valid string; a number
 * or a boolean may stand outside them; a missing path renders as nothing.
 */
export function renderTemplate(template: string, event: unknown, mode: "json" | "slack" = "json"): string {
  if (!template) return "";
  return template.replace(TOKEN_RE, (_token, path: string) =>
    mode === "slack" ? valueText(resolvePath(event, path)).replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;") : JSON.stringify(valueText(resolvePath(event, path))).slice(1, -1),
  );
}

export interface SamplePath {
  path: string;
  /** The sample's value there, as a template renders it. */
  example: string;
  /** A condition compares text, numbers and booleans. An object, a list or a null matches nothing. */
  comparable: boolean;
}

/** Every path a template can name in an event, with the sample's value at each: the values, and the
 *  lists whole, which a template renders as JSON. */
export function samplePaths(sample: unknown, prefix = ""): SamplePath[] {
  if (sample === null || typeof sample !== "object" || Array.isArray(sample)) return [];
  const out: SamplePath[] = [];
  for (const [key, value] of Object.entries(sample)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      out.push(...samplePaths(value, path));
    } else {
      out.push({ path, example: valueText(value), comparable: ["string", "number", "boolean"].includes(typeof value) });
    }
  }
  return out;
}

/** An event as a person reads it: indented JSON. */
export function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

// --- a contact's form answers ----------------------------------------------------------------------

/** Where a template or a condition names a contact's form answer: this, then the field's key
 *  (`FIELDS_PATH` in `platform`'s `services/shared/webhook_context.py`). */
export const FIELDS_PATH = "context.lead.fields.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * A sample with every field's example as its lead's answers: what a template's fields are picked
 * from and what it is previewed over. A real delivery carries only the answers its template names,
 * and a template can read no others, so the preview is what is sent. The whole event carries none:
 * it is shown as the sample itself.
 *
 * A sample that names no lead is returned as it is, as is any sample when the form has no fields.
 */
export function withFieldExamples(sample: unknown, fields: readonly WebhookField[]): unknown {
  if (!isRecord(sample) || !isRecord(sample.context) || !isRecord(sample.context.lead)) return sample;
  if (fields.length === 0) return sample;
  const answers = Object.fromEntries(fields.map((f) => [f.key, f.example ?? null]));
  return { ...sample, context: { ...sample.context, lead: { ...sample.context.lead, fields: answers } } };
}

/** The title of the form field a path names, or null for a path that names none of them. */
export function fieldTitle(path: string, fields: readonly WebhookField[]): string | null {
  const rest = path.replace(/^\$\./, "");
  if (!rest.startsWith(FIELDS_PATH)) return null;
  const key = rest.slice(FIELDS_PATH.length).split(".")[0];
  return fields.find((f) => f.key === key)?.title ?? null;
}
